import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { FileDTO, IUploadFile } from 'store/models/general-models';
import {
  errorToast,
  getAcceptTypes,
  handleUploadFiles,
  validateFiles,
} from 'utils';
import { AddButton, DndView, File } from './components';
import { Wrapper } from './file-uploader.styled';

import { IFileUploaderProps, IFileUploaderReturn } from './file-uploader.d';

/** Описание пропсов в file-uploader.d.ts */
export const FileUploader: FC<IFileUploaderProps> = observer(
  ({
    initialFiles = [],
    isMulti = false,
    isImageUploader = false,
    isVideoUploader = false,
    readOnly = false,
    showOnlyPublished = false,
    delayedUpload = false,
    view = 'list',
    limit,
    onUpload,
    onLimitError,
    onDownload,
    onComplete,
    onDragComplete,
    onDelete,
    onDeleteEnd,
  }): IFileUploaderReturn => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<IUploadFile[]>([]);

    const acceptTypes = useMemo(
      () => getAcceptTypes(isImageUploader, isVideoUploader),
      [isImageUploader, isVideoUploader]
    );

    const showAddButton = useMemo(() => {
      const hasNoFiles = !selectedFiles.length;
      const isViewAllowed = view === 'none' || view !== 'dnd' || hasNoFiles;

      return (
        !readOnly &&
        (isMulti || hasNoFiles) &&
        isViewAllowed &&
        selectedFiles.length !== limit
      );
    }, [readOnly, isMulti, selectedFiles.length, view, limit]);

    useEffect(() => {
      if (initialFiles) {
        let files = Array.isArray(initialFiles) ? initialFiles : [initialFiles];

        if (showOnlyPublished) {
          files = files.filter((file) =>
            'is_published' in file ? file.is_published : true
          );
        }

        setSelectedFiles(files);
      }
    }, [showOnlyPublished, initialFiles]);

    const onError = useCallback((fileName: string) => {
      setSelectedFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileName)
      );
    }, []);

    const handleChangeFiles = useCallback(
      async (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
          const files = Array.from(e.target.files);

          let validFiles = validateFiles(files, selectedFiles).map((file) => {
            const fileModel = FileDTO.create({
              ...file,
              isPending: !delayedUpload,
              isLocal: true,
            });
            fileModel.setOriginalFile(file);

            return fileModel;
          });

          if (!validFiles.length) {
            return;
          }

          if (limit && validFiles.length > limit - selectedFiles.length) {
            onLimitError?.(true);
            validFiles = validFiles.slice(0, limit - selectedFiles.length);
          }

          setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);

          !delayedUpload &&
            (await handleUploadFiles(validFiles, onUpload, onError));

          onComplete?.(validFiles);
        }
      },
      [
        selectedFiles,
        limit,
        delayedUpload,
        onUpload,
        onError,
        onComplete,
        onLimitError,
      ]
    );

    const onDeleteFile = useCallback(
      async (file: IUploadFile) => {
        if (onDelete && file.file && !file.isLocal) {
          try {
            file.setPending(true);
            await onDelete(file.file);

            setSelectedFiles((prevFiles) =>
              prevFiles.filter((item) => item.name !== file.name)
            );

            onDeleteEnd?.(file.file);
          } catch (error) {
            errorToast(
              'Произошла ошибка во время удаления. Попробуйте еще раз'
            );
          } finally {
            file.setPending(false);
          }
        } else {
          setSelectedFiles((prevFiles) =>
            prevFiles.filter((item) => item.name !== file.name)
          );
        }
      },
      [onDelete, onDeleteEnd]
    );

    return (
      <>
        <Wrapper isFullSize={view === 'dnd'}>
          {view === 'list' &&
            selectedFiles.map(
              (file: IUploadFile) =>
                !!file && (
                  <File
                    key={file.name}
                    file={file}
                    inProccess={file.isPending}
                    onDeleteFile={onDeleteFile}
                    readOnly={readOnly}
                    onDownload={onDownload}
                  />
                )
            )}

          {!!selectedFiles.length && view === 'dnd' && (
            <DndView
              ref={inputRef}
              files={selectedFiles}
              limit={limit}
              onDeleteFile={onDeleteFile}
              onDragComplete={onDragComplete}
            />
          )}
          {showAddButton && (
            <AddButton
              ref={inputRef}
              isMedia={isImageUploader || isVideoUploader}
              limit={limit}
            />
          )}
        </Wrapper>
        <input
          ref={inputRef}
          type={'file'}
          style={{ display: 'none' }}
          accept={acceptTypes}
          multiple={isMulti}
          onChange={handleChangeFiles}
        />
      </>
    );
  }
);
